import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';

import { PermissionsList } from './constants/permissionsList';
import { DrawerProvider } from './context/DrawerContext/DrawerContext';
import { ModalProvider } from './context/ModalContext';
import { NavigationBarProvider } from './context/NavigationBarContext';
import { ToastProvider } from './context/ToastContext';
import useIsSignedIn from './hooks/useIsSignedIn';
import { QueryProvider } from './queryProvider';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Chatbot } from '@/components/ChatBot/chatBot';
import { useMultiAppAutologin } from '@/hooks/useMultiAppAutologin';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import { useTokenAutoRefresh } from '@/hooks/useTokenAutoRefresh';
import { useTokenAutoRefreshOIDC } from '@/hooks/useTokenAutoRefreshOIDC';
import { refreshTokenConfig, oidcConfig } from '@/oidcConfig';
import AppRoutes from '@/routes';
import { clearOnLogOut } from '@/slices/authSlice';
import { AppDispatch, RootState, store } from '@/store';

const REFRESH_LOADER_TIMEOUT_MS = 15000;

/**
 * Token refresh must not run during multiApp handoff — it can clearOnLogOut
 * while loginSso / fetchUserProfile are still in flight.
 * Refresh hooks stay mounted here so needRefresh can still renew while the loader shows.
 */
function AppSession() {
  useTokenAutoRefreshOIDC();
  useTokenAutoRefresh();
  useShouldRefresh();

  const dispatch = useDispatch<AppDispatch>();
  const auth = useSelector((state: RootState) => state.auth);
  const userIsSignedIn = useIsSignedIn();
  const refreshConfig = refreshTokenConfig(auth);
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const chatbotAllowed = userIsSignedIn && auditableElements?.['CHAT_BOT'];
  const showRefreshLoader = userIsSignedIn && refreshConfig.needRefresh;

  useEffect(() => {
    if (!showRefreshLoader) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch(clearOnLogOut());
      localStorage.clear();
    }, REFRESH_LOADER_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [dispatch, showRefreshLoader]);

  if (showRefreshLoader) {
    return <ContainerLoader fullscreen />;
  }

  return (
    <>
      <AppRoutes />
      {chatbotAllowed && <Chatbot />}
    </>
  );
}

function WrappedApp() {
  const multiAppReady = useMultiAppAutologin();

  if (!multiAppReady) {
    return <ContainerLoader fullscreen />;
  }

  return <AppSession />;
}

function App() {
  return (
    <AuthProvider {...oidcConfig}>
      <Provider store={store}>
        <BrowserRouter>
          <DrawerProvider>
            <QueryProvider>
              <ModalProvider>
                <ToastProvider>
                  <NavigationBarProvider>
                    <WrappedApp />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </NavigationBarProvider>
                </ToastProvider>
              </ModalProvider>
            </QueryProvider>
          </DrawerProvider>
        </BrowserRouter>
      </Provider>
    </AuthProvider>
  );
}

export default App;
